import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import moment from 'moment-timezone';
import type { ReactNode } from 'react';
import {
  Button,
  Divider,
  Grid,
  Header,
  Icon,
  List,
  Modal,
  Popup,
  Segment,
} from 'semantic-ui-react';

import history from '@/history';
import type { Permissions } from '@models';
import {
  setWhatsNewDialog,
  setWhatsNewShowCtrl,
} from '@store/actions/action-whatsnew';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setViewState } from '@store/views.slice';

import { SocialIcons } from '../social-icons';
import type { WhatsNewData } from '../whats-new';

import s from './about-dialog.scss';

interface AboutDialogProps {
  permissions: Permissions | null;
  isLicenseGood: IsLicenseGood | false;
  uiCommitSha: string | null;
  uiImageBuildTimestamp: string | null;
  uiVersion: string;
  trigger: ReactNode;
  whatsNew: WhatsNewData | false;
}

const USER_TIMEZONE = moment.tz.guess();

// Factory that turns each entitlement into a list of <List.Item> elements
const makeEntitlementList = (
  arr: { name?: string; label?: string; description?: string; uri?: string }[],
) =>
  arr.map(item => {
    let el: ReactNode = null;
    if (item && typeof item.name === 'string' && item.name.trim().length) {
      const name = item.name.trim().toLowerCase();

      const label =
        typeof item.label === 'string' && item.label.trim().length
          ? item.label.trim()
          : name;

      const uri =
        typeof item.uri === 'string' && item.uri.trim().length
          ? item.uri.trim()
          : false;

      const desc =
        typeof item.description === 'string' && item.description.trim().length
          ? item.description.trim()
          : false;

      el = (
        <span className={classNames(s[`${name}Logo`], s.entitlementLogo)}>
          <span>{label}</span>
        </span>
      );
      if (uri) {
        el = (
          <a href={uri} target="_blank" rel="noopener noreferrer">
            {el}
          </a>
        );
      }
      el = (
        <List.Item
          className={s.listItem}
          key={!desc ? `entitlement_${name}` : null}
        >
          <Icon
            color="teal"
            size={!desc ? 'tiny' : undefined}
            name={desc ? 'info circle' : 'circle'}
          />
          <List.Content className={s.listItemContent}>{el}</List.Content>
        </List.Item>
      );

      if (desc) {
        el = (
          <Popup
            trigger={el}
            className={classNames(
              s.listItemPopup,
              'animate__animated animate__fadeIn',
            )}
            key={`entitlement_${name}`}
            position="left center"
            hideOnScroll
            hoverable
            content={
              <div>
                <Header as="h4">About {label}</Header>
                <p>{desc}</p>
              </div>
            }
          />
        );
      }
    }
    return el;
  });

const VersionInfoPopup = ({
  type,
  commitSha,
  imageBuildTimestamp,
}: {
  type: 'ui' | 'service';
  commitSha: string | null;
  imageBuildTimestamp: string | null;
}) =>
  commitSha || imageBuildTimestamp ? (
    <Popup
      wide="very"
      hoverable
      trigger={<Icon aria-label={`${type} version info`} name="info circle" />}
      position="top center"
      content={
        <div>
          <p>
            Commit SHA:&nbsp;
            <code>{commitSha || '---'}</code>
          </p>
          <p>
            Image built:&nbsp;
            {imageBuildTimestamp
              ? moment(imageBuildTimestamp)
                  .tz(USER_TIMEZONE)
                  .format('ddd, DD MMM YYYY [at] HH:mm:ss z')
              : '---'}
          </p>
        </div>
      }
    />
  ) : null;

export const AboutDialog = ({
  permissions,
  isLicenseGood,
  uiCommitSha,
  uiImageBuildTimestamp,
  uiVersion,
  whatsNew,
  trigger,
}: AboutDialogProps) => {
  useStyles(s);

  const dispatch = useAppDispatch();
  const auth = useAppSelector(state => state.auth);
  const isEngineVersionGood = useAppSelector(
    state => state.app.healthCheck.isEngineVersionGood,
  );

  const isOnboardingEnabled = useAppSelector(
    state => state.views.gettingStarted?.isEnabled ?? false,
  );
  const isWhatsNewCtrlShown = useAppSelector(
    state => state.app.whatsNew.isWhatsNewCtrlShown,
  );

  const { account, engineVersion, dbVersion } = auth;

  const {
    id,
    type,
    user,
    email,
    expiration,
    isBeforeWarning,
    organization,
    entitlements,
  } = isLicenseGood || { type: '' };

  // In reality this guard should never be hit,
  if (!account) {
    return <></>;
  }

  const {
    username,
    authUsername,
    authType,
    userCreatedAt,
    accountname,
    sessionStart,
    isAdmin,
    rbac,
    context,
  } = account;

  const acctCtx =
    context && context.accountname ? context.accountname : accountname;

  const hasPermissionsForOnboarding =
    permissions &&
    permissions.createRegistry &&
    permissions.createImage &&
    permissions.getActions;

  const { enterpriseCommitSha, enterpriseImageBuildTimestamp } =
    isEngineVersionGood || {
      enterpriseCommitSha: null,
      enterpriseImageBuildTimestamp: null,
    };

  return (
    <Modal
      role="dialog"
      id="about_client_modal"
      className="animate__animated animate__fadeInDownBig"
      size="small"
      closeIcon
      trigger={trigger}
    >
      <Header
        style={{
          fontWeight: 100,
          backgroundColor: 'rgb(54, 130, 224)',
          border: 0,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span className={s.aboutLogo} />
        <span data-test="about-client-modal" className={s.aboutTitle}>
          Enterprise Client
        </span>
      </Header>
      <Modal.Content style={{ overflow: 'hidden' }}>
        <Grid columns={2} divided>
          <Grid.Row>
            <Grid.Column width={8} verticalAlign="middle">
              <Grid>
                <Grid.Row>
                  <Grid.Column>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      className={classNames(
                        'animate__animated animate__bounceInDown',
                        s.sadRobot,
                      )}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns="equal">
                  {whatsNew && !isWhatsNewCtrlShown && (
                    <Grid.Column textAlign="center">
                      <Button
                        size="small"
                        color="green"
                        icon
                        labelPosition="right"
                        onClick={() => {
                          dispatch(setWhatsNewDialog(true));
                          dispatch(setWhatsNewShowCtrl(true));
                          const aboutClientModal =
                            document.getElementById('about_client_modal');
                          if (aboutClientModal && aboutClientModal.firstChild) {
                            (
                              aboutClientModal.firstChild as HTMLElement
                            ).click();
                          }
                        }}
                      >
                        What&#39;s New?
                        <Icon name="gift" />
                      </Button>
                    </Grid.Column>
                  )}
                  {!isOnboardingEnabled && hasPermissionsForOnboarding && (
                    <Grid.Column textAlign="center">
                      <Button
                        size="small"
                        color="grey"
                        icon
                        labelPosition="right"
                        onClick={() => {
                          // Restart the Onboarding steps
                          dispatch(
                            setViewState('gettingStarted', {
                              currentStep: 0,
                              completedSteps: 0,
                              isEnabled: true,
                            }),
                          );
                          // Close About modal
                          const aboutClientModal =
                            document.getElementById('about_client_modal');
                          if (aboutClientModal && aboutClientModal.firstChild) {
                            (
                              aboutClientModal.firstChild as HTMLElement
                            ).click();
                          }
                          // Navigate to Dashboard
                          history?.push('/dashboard');
                        }}
                      >
                        Getting Started
                        <Icon name="map marker alternate" />
                      </Button>
                    </Grid.Column>
                  )}
                </Grid.Row>
              </Grid>
            </Grid.Column>
            <Grid.Column width={8}>
              <Grid>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column>
                    <Icon name="tv" color="teal" />
                    &nbsp;&nbsp;Enterprise Client Version:&nbsp;
                    <code>{uiVersion}</code>
                    <VersionInfoPopup
                      type="ui"
                      commitSha={uiCommitSha}
                      imageBuildTimestamp={uiImageBuildTimestamp}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column>
                    <Icon name="plug" color="teal" />
                    &nbsp;&nbsp;Enterprise Service Version:&nbsp;
                    <code>{engineVersion}</code>
                    <VersionInfoPopup
                      type="service"
                      commitSha={enterpriseCommitSha}
                      imageBuildTimestamp={enterpriseImageBuildTimestamp}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column>
                    <Icon name="database" color="teal" />
                    &nbsp;&nbsp;Anchore DB Version:&nbsp;
                    <code>{dbVersion}</code>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column>
                    <Icon name="copyright outline" color="teal" />
                    &nbsp;&nbsp;
                    {moment().year()} Anchore Inc. All Rights Reserved
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Divider fitted horizontal className={s.divider}>
        <Icon circular inverted color="teal" name="user circle" />
      </Divider>
      <Modal.Content style={{ margin: 0, padding: '1rem' }}>
        <Grid columns={entitlements ? 2 : 1}>
          <Grid.Row verticalAlign="middle" divided>
            <Grid.Column width={entitlements ? 10 : 16}>
              <Grid columns={2}>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column width={6}>
                    <strong>User Created:</strong>
                  </Grid.Column>
                  <Grid.Column width={10}>
                    {moment(userCreatedAt)
                      .tz(USER_TIMEZONE)
                      .format('MMMM Do YYYY')}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column width={6}>
                    <strong>Username:</strong>
                  </Grid.Column>
                  <Grid.Column width={10}>
                    <code>{authUsername}</code>
                    &nbsp;&nbsp;
                    {isAdmin ? (
                      <span>
                        <Icon name="chess queen" color="teal" />
                        <span>Administrator</span>
                      </span>
                    ) : (
                      <span>
                        <Icon name="user circle" color="teal" />
                        <span>Standard User</span>
                      </span>
                    )}
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column width={6}>
                    <strong>Member of Account:</strong>
                  </Grid.Column>
                  <Grid.Column width={10}>
                    <code>{accountname}</code>
                    {(() => {
                      let permsEl: ReactNode = null;
                      if (!isAdmin) {
                        const perms = rbac
                          .find(item => item.account === accountname)
                          ?.permissions.map(permItem => (
                            <List.Item as="li" key={permItem.action}>
                              <code>
                                {permItem.action === '*'
                                  ? 'All Permissions (Full Control)'
                                  : permItem.action}
                              </code>
                            </List.Item>
                          ));
                        permsEl = (
                          <Popup
                            content={
                              <div>
                                <div>
                                  Listed below are the permissions granted for
                                  use within account&nbsp;
                                  <code>
                                    <strong>{accountname}</strong>
                                  </code>
                                  :
                                </div>
                                <List as="ul">{perms}</List>
                              </div>
                            }
                            position="right center"
                            trigger={
                              <span className={s.viewPermsLink}>
                                View Permissions
                              </span>
                            }
                          />
                        );
                      }
                      return permsEl;
                    })()}
                  </Grid.Column>
                </Grid.Row>
                {acctCtx !== accountname ? (
                  <Grid.Row className={s.aboutItem}>
                    <Grid.Column width={6}>
                      <strong>Current Account Context:</strong>
                    </Grid.Column>
                    <Grid.Column width={10}>
                      <code>{acctCtx}</code>
                      {(() => {
                        let permsEl: ReactNode = null;
                        if (!isAdmin) {
                          const perms = rbac
                            .find(item => item.account === acctCtx)
                            ?.permissions.map(permItem => (
                              <List.Item as="li" key={permItem.action}>
                                <code>{permItem.action}</code>
                              </List.Item>
                            ));
                          permsEl = (
                            <Popup
                              content={
                                <div>
                                  <div>
                                    Listed below are the permissions granted for
                                    use within account&nbsp;
                                    <code>
                                      <strong>{acctCtx}</strong>
                                    </code>
                                    :
                                  </div>
                                  <List as="ul">{perms}</List>
                                </div>
                              }
                              position="right center"
                              trigger={
                                <span className={s.viewPermsLink}>
                                  View Permissions
                                </span>
                              }
                            />
                          );
                        }
                        return permsEl;
                      })()}
                    </Grid.Column>
                  </Grid.Row>
                ) : null}
                {authUsername !== username && authType !== 'jwt' ? (
                  <Grid.Row className={s.aboutItem}>
                    <Grid.Column width={6}>
                      <strong>User Mapping:</strong>
                    </Grid.Column>
                    <Grid.Column width={10}>
                      <code>{username}</code>
                      <Popup
                        position="right center"
                        trigger={
                          <Icon
                            className={s.mappingInfoIcon}
                            color="orange"
                            name="question circle"
                          />
                        }
                        content={
                          <span>
                            User <strong>{authUsername}</strong> is managed by
                            an external directory server, but is associated with
                            the analysis data stored in an Anchore Enterprise
                            Services account&nbsp;
                            <strong>{accountname}</strong> by user mapping&nbsp;
                            <strong>{username}</strong>.
                          </span>
                        }
                      />
                    </Grid.Column>
                  </Grid.Row>
                ) : null}
                <Grid.Row className={s.aboutItem}>
                  <Grid.Column width={6}>
                    <strong>Current Session Started:</strong>
                  </Grid.Column>
                  <Grid.Column width={10}>
                    {moment(sessionStart)
                      .tz(USER_TIMEZONE)
                      .format('ddd, DD MMM YYYY [at] HH:mm:ss z')}{' '}
                    ({moment(sessionStart).fromNow()})
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Grid.Column>
            {entitlements ? (
              <Grid.Column width="6" className={s.entitlementsCol}>
                <List className={s.listWrapperItem}>
                  <List.Header className={s.listHeaderItem}>
                    Entitlements
                  </List.Header>
                  {makeEntitlementList(entitlements)}
                </List>
                <span className={s.spaceMan} />
                <span className={s.cameraTripod} />
              </Grid.Column>
            ) : null}
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Divider fitted horizontal className={s.divider}>
        <Icon circular inverted color="teal" name="drivers license" />
      </Divider>
      <Modal.Content style={{ margin: 0, padding: '1rem' }}>
        <Grid columns={2}>
          <Grid.Row className={s.aboutItem}>
            <Grid.Column width={3}>
              <strong>License ID:</strong>
            </Grid.Column>
            <Grid.Column width={13}>
              <code>{id}</code>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className={s.aboutItem}>
            <Grid.Column width={3}>
              <strong>License Type:</strong>
            </Grid.Column>
            <Grid.Column width={13}>
              <code>{`${type.charAt(0).toUpperCase()}${type.substr(1)}`}</code>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className={s.aboutItem}>
            <Grid.Column width={3}>
              <strong>Expires:</strong>
            </Grid.Column>
            <Grid.Column width={13}>
              {moment(expiration)
                .tz(USER_TIMEZONE)
                .format('ddd, DD MMM YYYY [at] HH:mm:ss z')}
              &nbsp;(
              {moment(expiration).fromNow()})
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className={s.aboutItem}>
            <Grid.Column width={3}>
              <strong>Licensed to:</strong>
            </Grid.Column>
            <Grid.Column width={13}>
              {user}
              &nbsp;
              <a href={`mailto:${email}`}>
                <Icon name="mail" color="teal" />
                {email}
              </a>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row className={s.aboutItem}>
            <Grid.Column width={3}>
              <strong>Organization:</strong>
            </Grid.Column>
            <Grid.Column width={13}>{organization}</Grid.Column>
          </Grid.Row>
        </Grid>

        {!isBeforeWarning && (
          <Segment
            size="tiny"
            inverted
            secondary
            color="red"
            textAlign="center"
            className="animate__animated animate__pulse"
          >
            <Icon name="warning sign" />
            Your license will expire&nbsp;
            <strong>{moment(expiration).fromNow()}</strong>
            —contact Anchore Support for a renewal!
          </Segment>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Grid inverted columns="equal">
          <Grid.Column verticalAlign="middle">
            <SocialIcons />
          </Grid.Column>
          <Grid.Column verticalAlign="middle">
            <Button
              as="a"
              size="tiny"
              href="https://support.anchore.com"
              icon
              color="blue"
              target="_blank"
              labelPosition="right"
              style={{ margin: 0 }}
            >
              <Icon name="external alternate" />
              Visit Anchore Support
            </Button>
          </Grid.Column>
        </Grid>
      </Modal.Actions>
    </Modal>
  );
};
